import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Check, ArrowRight } from "lucide-react";
import axios from "axios";
import { scrollTo } from "./Header";
import { API } from "../../lib/api";

const shopIncludes = ["Custom homepage on your domain", "Complete shop & ordering system", "Payments: cards, wallets, cash", "No-code page builder", "Admin app: orders, printing, controls", "SEO suite & CMS pages", "QR dine-in & table reservations"];
const appsIncludes = ["Everything in the Shop System", "Native iOS & Android customer apps", "Published under your brand", "Push notifications & flash deals", "App banners & version control", "One-tap re-ordering & fast checkout"];

const fmt = (n) => n.toLocaleString("de-CH", { maximumFractionDigits: 0 });

function PriceCard({ testid, title, tagline, price, original, includes, featured, cta, delay }) {
  return (
    <motion.div
      data-testid={testid}
      initial={{ opacity: 0, y: 50 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.8, delay }}
      className={`relative rounded-xl border p-10 lg:p-14 flex flex-col ${
        featured ? "border-brand bg-brand/5 card-glow-strong" : "border-ink/10 bg-white"
      }`}
    >
      {featured && (
        <span className="absolute -top-3.5 left-10 bg-brand text-white font-mono text-[10px] tracking-[0.2em] uppercase px-4 py-1.5 rounded-full">
          Most popular
        </span>
      )}
      <h3 className="font-display font-black uppercase tracking-tight text-2xl lg:text-3xl text-ink">{title}</h3>
      <p className="mt-2 font-mono text-xs tracking-[0.15em] uppercase text-ink/50">{tagline}</p>
      <div className="mt-8 flex items-end gap-3 flex-wrap">
        {original && <span className="font-display font-bold text-2xl text-ink/35 line-through mb-2">CHF {fmt(original)}</span>}
        <span data-testid={`${testid}-price`} className="font-display font-black tracking-tighter leading-none text-6xl lg:text-7xl text-ink">
          <span className="text-2xl align-top mr-1 text-brand">CHF</span>{fmt(price)}
        </span>
        <span className="font-mono text-xs uppercase tracking-[0.2em] text-brand mb-2">one-time</span>
      </div>
      <ul className="mt-10 space-y-3.5 flex-1">
        {includes.map((item) => (
          <li key={item} className="flex items-start gap-3 text-sm text-ink/70 font-light">
            <Check className="w-4 h-4 text-brand mt-0.5 shrink-0" />
            {item}
          </li>
        ))}
      </ul>
      <button
        data-testid={`${testid}-cta`}
        onClick={() => scrollTo("#contact")}
        className={`group mt-10 flex items-center justify-center gap-3 font-display font-bold uppercase tracking-wide px-8 py-4 rounded-full transition-colors duration-300 ${
          featured ? "bg-brand hover:bg-ink text-white" : "border border-ink/20 text-ink hover:border-brand hover:text-brand"
        }`}
      >
        {cta}
        <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
      </button>
    </motion.div>
  );
}

export default function Pricing() {
  const [cfg, setCfg] = useState({ shop_price: 2450, apps_price: 3980, annual_price: 380, discount_percent: 0, discount_active: false, discount_label: "" });

  useEffect(() => {
    axios.get(`${API}/pricing`).then((r) => setCfg(r.data)).catch(() => {});
  }, []);

  const disc = cfg.discount_active && cfg.discount_percent > 0 ? cfg.discount_percent / 100 : 0;
  const shopPrice = disc ? Math.round(cfg.shop_price * (1 - disc)) : cfg.shop_price;
  const appsPrice = disc ? Math.round(cfg.apps_price * (1 - disc)) : cfg.apps_price;

  return (
    <section id="pricing" data-testid="pricing-section" className="max-w-7xl mx-auto px-6 lg:px-12 py-28 lg:py-40">
      <motion.p
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.7 }}
        className="font-mono text-xs tracking-[0.3em] uppercase text-brand mb-6"
      >
        The Deal
      </motion.p>
      <motion.h2
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.8, delay: 0.1 }}
        className="font-display font-black uppercase tracking-tighter leading-[0.95] text-4xl sm:text-5xl lg:text-6xl max-w-4xl text-ink"
      >
        Pay once. <span className="text-outline">Never again.</span>
      </motion.h2>

      {disc > 0 && (
        <motion.div
          data-testid="discount-banner"
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="mt-10 inline-flex items-center gap-3 border border-brand/50 bg-brand/5 rounded-full px-6 py-3"
        >
          <span className="w-2 h-2 rounded-full bg-brand animate-pulse" />
          <span className="font-mono text-xs tracking-[0.2em] uppercase text-ink">{cfg.discount_label || `${cfg.discount_percent}% launch discount`} — {cfg.discount_percent}% off, limited time</span>
        </motion.div>
      )}

      <div className="mt-16 grid grid-cols-1 lg:grid-cols-2 gap-8">
        <PriceCard
          testid="pricing-shop-card"
          title="The Shop System"
          tagline="Homepage + ordering, fully yours"
          price={shopPrice}
          original={disc ? cfg.shop_price : null}
          includes={shopIncludes}
          cta="Start with the shop"
          delay={0.1}
        />
        <PriceCard
          testid="pricing-apps-card"
          title="Shop + Mobile Apps"
          tagline="The complete ownership package"
          price={appsPrice}
          original={disc ? cfg.apps_price : null}
          includes={appsIncludes}
          featured
          cta="Own everything"
          delay={0.2}
        />
      </div>

      <motion.div
        data-testid="annual-note"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 0.8, delay: 0.3 }}
        className="mt-10 rounded-xl border border-ink/10 bg-white px-8 py-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4"
      >
        <p className="text-ink/70 font-light text-sm md:text-base">
          Running costs: <span className="font-display font-bold text-ink">CHF {fmt(cfg.annual_price)}.- / year</span> — hosting, domain &amp; support. That's it.
        </p>
        <p className="font-mono text-xs tracking-[0.2em] uppercase text-brand">Live in under 7 days &nbsp;·&nbsp; 0% commission, ever</p>
      </motion.div>
    </section>
  );
}
